/**
 * AppDownloadButton — Marketing CTA shown at the top of the Create Account
 * page (and any other public marketing surface that opts in).
 *
 * Behaviour:
 *  • If an APK has been uploaded by the admin (`/api/admin/app-apk` shows
 *    `available: true`) the button hits `/api/download/app` which streams
 *    the file as a real download (Content-Disposition: attachment).
 *  • Otherwise, if a `play_store_url` is configured in App Version Gate, the
 *    button falls back to that URL (Play Store / hosted page).
 *  • If neither is set, the button hides itself.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DownloadSimple } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AppDownloadButton = () => {
  const [apkAvailable, setApkAvailable] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // 1) Check whether an APK is hosted on our own backend via a tiny
        //    public status endpoint (HEAD on /api/download/app is often
        //    blocked by proxies/CDNs, so we use a dedicated GET).
        const { data } = await axios.get(`${BACKEND_URL}/api/download/app/status`);
        if (alive && data?.available) {
          setApkAvailable(true);
          return;
        }
      } catch {
        // ignore — fall through to fallback URL
      }
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/app-version`);
        if (alive && data?.play_store_url) setFallbackUrl(data.play_store_url);
      } catch {
        // App-version endpoint is non-critical — render nothing on error.
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!apkAvailable && !fallbackUrl) return null;

  // When the APK is hosted on our backend we link directly to the download
  // endpoint. The browser triggers a real file save thanks to the
  // Content-Disposition: attachment header set by the backend.
  const href = apkAvailable ? `${BACKEND_URL}/api/download/app` : fallbackUrl;

  return (
    <div className="mb-5 flex justify-center" data-testid="app-download-banner">
      <a
        href={href}
        target={apkAvailable ? '_self' : '_blank'}
        rel="noopener noreferrer"
        download={apkAvailable ? 'beeweddy.apk' : undefined}
        className="group inline-flex items-center gap-3 px-5 py-3 rounded-full bg-text-primary text-sand-50 hover:bg-brand-primary transition-colors shadow-md hover:shadow-lg"
        data-testid="app-download-btn"
      >
        <DownloadSimple size={22} weight="bold" className="text-sand-50" />
        <span className="text-left leading-tight">
          <span className="block text-[10px] uppercase tracking-[0.2em] text-sand-50/70">
            Get the BeeWeddy app
          </span>
          <span className="block text-sm font-bold">Download App</span>
        </span>
      </a>
    </div>
  );
};

export default AppDownloadButton;
